import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/Common/Modal';
import { Chatbot } from '@/services/chatbots';
import { Badge } from '@/components/Common/badge';
import { Bot, MessageCirclePlus, Loader2 } from 'lucide-react';

interface ChatbotSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  chatbots: Chatbot[];
  onSelect: (chatbot: Chatbot) => void;
}

export const ChatbotSelectorDialog: React.FC<ChatbotSelectorDialogProps> = ({
  open,
  onClose,
  chatbots,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleSelect = (chatbot: Chatbot) => {
    setSelectedId(chatbot.id);
    // Small delay for visual feedback
    setTimeout(() => {
      onSelect(chatbot);
      setSelectedId(null);
    }, 150);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={(
        <span className="inline-flex items-center gap-2">
          <MessageCirclePlus className="h-5 w-5 text-primary" />
          {t('chatbots.selector.title')}
        </span>
      )}
      description={t('chatbots.selector.description')}
    >
      <div className="space-y-4">
        {chatbots.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>{t('chatbots.selector.empty')}</p>
            <p className="text-sm mt-2">{t('chatbots.selector.emptyHint')}</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {chatbots.map((chatbot) => (
              <div
                key={chatbot.id}
                onClick={() => handleSelect(chatbot)}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all
                  hover:border-primary hover:shadow-md
                  ${chatbot.is_default ? 'border-primary/50 bg-primary/5' : 'border-border'}
                  ${selectedId === chatbot.id ? 'opacity-50 pointer-events-none' : ''}
                `}
              >
                <div className="flex items-start gap-3">
                  <Bot className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{chatbot.name}</h3>
                      {chatbot.is_default && (
                        <Badge variant="secondary" className="text-xs">
                          {t('chatbots.selector.defaultBadge')}
                        </Badge>
                      )}
                      {chatbot.visibility === 'organization' && (
                        <Badge variant="outline" className="text-xs">
                          {t('chatbots.page.organizationBadge')}
                        </Badge>
                      )}
                      {selectedId === chatbot.id && (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      )}
                    </div>
                    {chatbot.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {chatbot.description}
                      </p>
                    )}
                    {!chatbot.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {t('chatbots.page.noDescription')}
                      </p>
                    )}
                    {(chatbot.theme_filters?.length || chatbot.folder_paths?.length) ? (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {chatbot.theme_filters && chatbot.theme_filters.length > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                            {t('chatbots.page.themesCount', { count: chatbot.theme_filters.length })}
                          </span>
                        )}
                        {chatbot.folder_paths && chatbot.folder_paths.length > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300">
                            {t('chatbots.page.foldersCount', { count: chatbot.folder_paths.length })}
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};
